import { useEffect, useRef, useState } from 'react';

const DEFAULT_INITIAL_COUNT = 10;
const DEFAULT_BATCH_SIZE = 10;

/**
 * Parameters for {@link useChunkedItems}.
 *
 * @template T - The type of items in the array.
 */
interface UseChunkedItemsParams<T> {
  /**
   * The full array of items to progressively render.
   * Changing this reference (e.g. a new API result, page change, or filter change)
   * automatically resets rendering back to {@link initialCount}.
   */
  items?: T[];
  /**
   * Number of items to render on the first frame.
   * Should match the skeleton loader count to avoid a height shift at the
   * loader-to-content transition.
   * @default 10
   */
  initialCount?: number;
  /**
   * Number of additional items to reveal on each subsequent animation frame.
   * @default 10
   */
  batchSize?: number;
}

/**
 * Return value of {@link useChunkedItems}.
 *
 * @template T - The type of items in the array.
 */
interface UseChunkedItemsResult<T> {
  /**
   * The currently visible prefix of {@link UseChunkedItemsParams.items items}.
   * Grows by {@link UseChunkedItemsParams.batchSize batchSize} per animation frame
   * until it equals the full array.
   *
   * Index positions are preserved relative to the original array, so any
   * parallel index-aligned arrays (e.g. `expandByIndex`) remain correctly aligned.
   */
  visibleItems: T[];
  /**
   * `true` while `visibleItems.length < items.length`; `false` once all items are
   * visible or when `items` is empty/undefined.
   *
   * Use this to render trailing placeholder skeletons, disable batch-level
   * controls, or show a progress indicator.
   */
  isChunking: boolean;
}

/**
 * Progressively reveals items across multiple animation frames to avoid blocking
 * the main thread when a large list mounts all at once.
 *
 * On each `requestAnimationFrame`, {@link UseChunkedItemsParams.batchSize batchSize}
 * more items are added to the visible slice until the full list is mounted. Resets to
 * {@link UseChunkedItemsParams.initialCount initialCount} whenever the
 * {@link UseChunkedItemsParams.items items} reference changes.
 *
 * Compatible with any parallel index-aligned array (e.g. `expandByIndex`) because
 * {@link UseChunkedItemsResult.visibleItems visibleItems} is always a stable prefix
 * slice of the original array, preserving original index positions.
 *
 * @template T - The type of items in the array.
 *
 * @param params - Configuration for chunked rendering.
 * @param params.items        The full array to render progressively. A new reference
 *                            triggers a reset to `initialCount`.
 * @param params.initialCount Number of items to render on the first frame. Defaults to 10.
 * @param params.batchSize    Additional items revealed per animation frame. Defaults to 10.
 *
 * @returns An object containing `visibleItems` (the current prefix slice) and
 *          `isChunking` (whether more items remain to be revealed).
 *
 * @example
 * const { visibleItems, isChunking } = useChunkedItems({
 *   items: currentInventories,
 *   initialCount: 10,
 *   batchSize: 10,
 * });
 *
 * return <>
 *   {visibleItems.map((item, index) => <MyCard key={item.id} item={item} />)}
 *   {isChunking && <MySkeletonLoader />}
 * </>
 */
const useChunkedItems = <T>({
  items,
  initialCount = DEFAULT_INITIAL_COUNT,
  batchSize = DEFAULT_BATCH_SIZE,
}: UseChunkedItemsParams<T>): UseChunkedItemsResult<T> => {
  const [visibleCount, setVisibleCount] = useState(initialCount);

  /**
   * Monotonically increasing counter, incremented each time `items` changes.
   *
   * Each rAF callback captures this value at scheduling time. Before applying
   * its state update, the callback compares its captured value against the
   * current ref value. If they differ, `items` has changed since the callback
   * was scheduled and the update is discarded — preventing a stale rAF from a
   * previous dataset from corrupting the visible count of the new dataset.
   */
  const generationRef = useRef(0);

  // Reset the visible count whenever a new dataset arrives.
  useEffect(() => {
    generationRef.current += 1;
    setVisibleCount(initialCount);
  }, [items, initialCount]);

  // Schedule the next chunk if items remain to be revealed.
  useEffect(() => {
    if (!items?.length || visibleCount >= items.length) return;

    const generation = generationRef.current;

    const frameId = requestAnimationFrame(() => {
      // Guard: if `items` changed between scheduling and firing, discard.
      if (generationRef.current !== generation) return;
      setVisibleCount(prev => Math.min(prev + batchSize, items.length));
    });

    return () => cancelAnimationFrame(frameId);
  }, [items, visibleCount, batchSize]);

  return {
    visibleItems: items?.slice(0, visibleCount) ?? [],
    isChunking: !!items?.length && visibleCount < items.length,
  };
};

export { useChunkedItems };
export type { UseChunkedItemsParams, UseChunkedItemsResult };

